import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { GuardianContext } from '../../components/GuardianProvider';
import type { GuardianAgent } from '../../components/message-composer/chat-context/selectors';

/** Backend `name` of the read-only dashboard analysis agent (forced in dashboard mode). */
export const DASHBOARD_AGENT_ID = 'dashboard_agent';

const DASHBOARD_AGENT: GuardianAgent = {
  id: DASHBOARD_AGENT_ID,
  name: 'Dashboard',
  description: '',
  icon: 'Dashboard',
  parent: null,
  // Temporary landing card shown before the auto-summary lands.
  landingPage: {
    type: 'markdownChatCard',
    title: 'Dashboard analysis',
    tagline: 'Ask me about the charts on this dashboard.',
    content:
      'I can analyze the charts on this dashboard — spotting trends, spikes, and anomalies, ' +
      'and explaining what each metric means. Just ask about anything you see.',
  },
  isSelectable: true,
};

/**
 * Wires the chat context for a dashboard
 * The instance scope (shared with the chat via the URL) is owned by the filter bar
 */
export const useDashboardConversation = (templateId: string | undefined): void => {
  const { setDashboardConversationId, setSelectedAgent } = useContext(GuardianContext);

  // One conversation per dashboard, kept for the session so navigating away and back keeps it.
  const { data: conversationId } = useQuery<string>({
    queryKey: ['dashboards', 'conversationId', templateId],
    enabled: false,
    initialData: uuidv4,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });

  useEffect(() => {
    if (!templateId) return;
    setDashboardConversationId(conversationId);
  }, [templateId, conversationId, setDashboardConversationId]);

  useEffect(() => {
    if (!templateId) return;
    setSelectedAgent(DASHBOARD_AGENT);
  }, [templateId, setSelectedAgent]);
};
